import { netBase } from '../discord/embedded.js';
import { useRoom } from './useRoom.js';

/**
 * trackClient — fire-and-forget client-side analytics beacon.
 *
 * POSTs to the server's `/api/track` route (whitelisted event names only —
 * see server discordRoute/analytics). Reads the room code + session token
 * imperatively from the useRoom store so any component (or plain function)
 * can call it without wiring props. Guarantees, in order of importance:
 *
 *  - NEVER throws and never surfaces an error — analytics must not be able to
 *    break gameplay. A failed POST (offline, MockDriver with no server, an old
 *    server without the route) is silently dropped.
 *  - No-op when not in a room: without a code + session token the server
 *    could not attribute the event anyway.
 */
export type TrackedClientEvent =
  | 'invite_opened'
  | 'stall_nudge_acted'
  | 'community_cta_clicked'
  | 'standing_viewed'
  | 'bm_upsell_shown'
  | 'bm_upsell_acted'
  | 'next_show_copied'
  | 'playbill_copied';

export function trackClient(ev: TrackedClientEvent, props?: Record<string, unknown>): void {
  try {
    const { code, sessionToken } = useRoom.getState();
    if (!code || !sessionToken) return;
    void fetch(`${netBase()}/api/track`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ room: code, token: sessionToken, ev, ...(props ? { props } : {}) }),
      // Let the beacon survive a navigation right after the click (invite CTA).
      keepalive: true,
    }).catch(() => {
      /* fire-and-forget */
    });
  } catch {
    /* fire-and-forget — never let telemetry break the game */
  }
}
